/* eslint-disable react/jsx-key */

import type { ReactNode } from 'react';
import React from 'react';
import { styled, useStyletron, withStyle } from 'baseui';
import { Block } from 'baseui/block';
import type { StyleObject } from 'styletron-react';
import { GlbRender } from './renderers/3d';
import Audio from './renderers/audio/audio';
import DataComponent from './renderers/data/data';
import HTMLRender from './renderers/html/html';
import { ImageRenderer } from './renderers/image/image-renderer.react';
import Link from './renderers/link/link';
import MarkDown from './renderers/markdown/markdown';
import PDFRenderer from './renderers/pdf/pdf-renderer';
import { RemoteText } from './renderers/remote-text/remote-text';
import Video from './renderers/video/video';
type JSON = null | void | string | number | boolean | {
  [x: string]: JSON;
} | Array<JSON>;
type StyleOverrides = {
  KeyContainer?: StyleObject;
  ValueContainer?: StyleObject;
  ParentContainer?: StyleObject;
};
type onImageSelectionT = (imageData: string) => void;
export type Props = {
  // @ts-expect-error todo(ts-migration) TS2411 Property 'styleOverrides' of type 'StyleOverrides' is not assignable to 'string' index type 'JSON'.
  styleOverrides?: StyleOverrides;
  // @ts-expect-error todo(ts-migration) TS2411 Property 'onImageSelection' of type 'onImageSelectionT' is not assignable to 'string' index type 'JSON'.
  onImageSelection?: onImageSelectionT;
  enableImageSelection?: boolean;
  options?: OptionsT;
  noOfImageGrids?: number;
  downloadFile?: boolean;
  [x: string]: JSON;
};
type OptionsT = {
  videoPlaybackSpeed?: number;
};

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const MainContainer = styled(Block, ({
  $theme
}) => ({
  color: $theme.colors.contentPrimary,
  padding: $theme.sizing.scale400
}));

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const StyledKeyValue = styled<typeof Block, {
  $overrides?: StyleObject;
}>(Block, ({
  $theme,
  $overrides
}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  paddingBottom: $theme.sizing.scale400,
  ...$overrides
}));

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const StyledKey = styled<typeof Block, {
  $overrides;
}>(Block, ({
  $theme,
  $overrides
}) => ({
  flex: '0 0 30%',
  fontWeight: 'bold',
  textTransform: 'capitalize',
  wordBreak: 'break-word',
  ...$overrides
}));

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const StyledValue = styled<typeof Block, {
  $overrides;
}>(Block, ({
  $theme,
  $overrides
}) => ({
  flex: '1 1 auto',
  overflow: 'auto',
  ...$overrides
}));
const JsonKey = withStyle(StyledKey, ({
  $theme,
  $overrides
}) => ({
  flex: '1 0 auto',
  borderBottom: `1px solid ${$theme.colors.contentSecondary}`,
  paddingBottom: $theme.sizing.scale200,
  ...$overrides
}));

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const JsonValue = styled(Block, ({
  $theme
}) => ({
  paddingLeft: '5%'
}));

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const MediaContainer = styled(Block, ({
  $theme
}) => ({
  maxWidth: '500px',
  position: 'relative'
}));
const TextBlock = styled(Block, {
  whiteSpace: 'pre-wrap'
});
const GridWrapper = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  width: '100%',
  padding: `${$theme.sizing.scale400} 0px`,
  gap: $theme.sizing.scale400
}));
const isPrimitive = (arg: any): boolean => {
  const type = typeof arg;
  return arg === null || type !== 'object' && type !== 'function';
};
const JSONBlock = ({
  title,
  children,
  styleOverrides: {
    KeyContainer = {}
  } = {}
}: {
  title: string;
  children: ReactNode;
  styleOverrides?: StyleOverrides;
}) => {
  return <Block>
      <StyledKeyValue>
        <JsonKey $overrides={KeyContainer}>{title}</JsonKey>
      </StyledKeyValue>
      <JsonValue>{children}</JsonValue>
    </Block>;
};
const KeyValue = ({
  title,
  children,
  styleOverrides: {
    KeyContainer = {},
    ValueContainer = {},
    ParentContainer = {}
  } = {}
}: {
  title: string;
  children: ReactNode;
  styleOverrides?: StyleOverrides;
}) => {
  return <StyledKeyValue $overrides={ParentContainer}>
      <StyledKey $overrides={KeyContainer}>{title}</StyledKey>
      <StyledValue $overrides={ValueContainer}>{children}</StyledValue>
    </StyledKeyValue>;
};
const TYPE_EXCLUDE_MEDIA_CONTAINER = ['3D', 'md', 'pdf', 'html'];
const MediaRenderComponent = ({
  title,
  styleOverrides,
  children,
  type
}: {
  title: string;
  children: ReactNode;
  styleOverrides?: StyleOverrides;
  type?: string;
}) => {
  return <KeyValue title={title} styleOverrides={styleOverrides}>
      {type && TYPE_EXCLUDE_MEDIA_CONTAINER.includes(type) ? <Block>{children}</Block> : <MediaContainer>{children}</MediaContainer>}
    </KeyValue>;
};
type CustomRendererT = (key: string, value: string, options: OptionsT, styleOverrides: StyleOverrides, onImageSelection?: onImageSelectionT, enableImageSelection?: boolean, downloadFile?: boolean) => ReactNode;
const CUSTOM_PROPERTY_PREFIX = {
  TEXT: 'text_',
  IMG: 'img_',
  LINK: 'link_',
  AUDIO: 'audio_',
  VIDEO: 'video_',
  THREE_D: '3D_',
  MD: 'md_',
  HTML: 'html_',
  TEXT_URL: 'url_text_',
  PDF: 'pdf_'
};
const CustomRenderer: {
  [x: string]: CustomRendererT;
} = {
  [CUSTOM_PROPERTY_PREFIX.TEXT]: (key, value, options, styleOverrides, onImageSelection, enableImageSelection) => <MediaRenderComponent
  // @ts-expect-error missing null check
  title={key.split(CUSTOM_PROPERTY_PREFIX.TEXT).pop()} styleOverrides={styleOverrides}>
      <TextBlock>{value}</TextBlock>
    </MediaRenderComponent>,
  [CUSTOM_PROPERTY_PREFIX.IMG]: (key, value, options, styleOverrides, onImageSelection, enableImageSelection) => <MediaRenderComponent
  // @ts-expect-error missing null check
  title={key.split(CUSTOM_PROPERTY_PREFIX.IMG).pop()} styleOverrides={styleOverrides}>
      <ImageRenderer src={value} onSelect={onImageSelection} enableImageSelection={enableImageSelection} />
    </MediaRenderComponent>,
  link_: (key, value, options, styleOverrides, onImageSelection, enableImageSelection) => <MediaRenderComponent
  // @ts-expect-error missing null check
  title={key.split(CUSTOM_PROPERTY_PREFIX.LINK).pop()} styleOverrides={styleOverrides}>
      {/* $FlowFixMe This comment suppresses an error found when enabling type
       * checking of the legacy common/shared/ packages in the monorepo. To
       * view the error, delete this comment and run Flow. */}
      <Link href={value} target="_blank" rel="noopener noreferrer">
        Click Here
      </Link>
    </MediaRenderComponent>,
  audio_: (key, value, options, styleOverrides, onImageSelection, enableImageSelection) => <MediaRenderComponent
  // @ts-expect-error missing null check
  title={key.split(CUSTOM_PROPERTY_PREFIX.AUDIO).pop()} styleOverrides={styleOverrides}>
      <Audio src={value} />
    </MediaRenderComponent>,
  video_: (key, value, options, styleOverrides, onImageSelection, enableImageSelection) => <MediaRenderComponent
  // @ts-expect-error missing null check
  title={key.split(CUSTOM_PROPERTY_PREFIX.VIDEO).pop()} styleOverrides={styleOverrides}>
      <Video src={value} videoPlaybackSpeed={options.videoPlaybackSpeed} />
    </MediaRenderComponent>,
  ['3D_']: (key, value, styleOverrides, onImageSelection, enableImageSelection) => {
    if (!value.includes('terrablob')) {
      return <MediaRenderComponent
      // @ts-expect-error missing null check
      title={key.split(CUSTOM_PROPERTY_PREFIX.THREE_D).pop()} styleOverrides={{
        KeyContainer: {
          fontWeight: 'normal'
        }
      }} type={'3D'}>
          <GlbRender url={value}></GlbRender>
        </MediaRenderComponent>;
    }
  },
  [CUSTOM_PROPERTY_PREFIX.MD]: (key, value, options, styleOverrides) => {
    return <MediaRenderComponent
    // @ts-expect-error missing null check
    title={key.split(CUSTOM_PROPERTY_PREFIX.MD).pop()} styleOverrides={styleOverrides} type={'md'}>
        <MarkDown src={value} />
      </MediaRenderComponent>;
  },
  [CUSTOM_PROPERTY_PREFIX.HTML]: (key, value, options, styleOverrides) => {
    return <MediaRenderComponent
    // @ts-expect-error missing null check
    title={key.split(CUSTOM_PROPERTY_PREFIX.HTML).pop()} styleOverrides={styleOverrides} type={'html'}>
        <HTMLRender src={value} />
      </MediaRenderComponent>;
  },
  [CUSTOM_PROPERTY_PREFIX.TEXT_URL]: (key, value, options, styleOverrides) => {
    return <MediaRenderComponent
    // @ts-expect-error missing null check
    title={key.split(CUSTOM_PROPERTY_PREFIX.MD).pop()} styleOverrides={styleOverrides} type={'md'}>
        <RemoteText textUrl={value} />
      </MediaRenderComponent>;
  },
  [CUSTOM_PROPERTY_PREFIX.PDF]: (key, value, options, styleOverrides, onImageSelection, enableImageSelection, downloadFile) => {
    return <MediaRenderComponent
    // @ts-expect-error missing null check
    title={key.split(CUSTOM_PROPERTY_PREFIX.PDF).pop()} styleOverrides={{
      ...styleOverrides,
      ValueContainer: {
        width: '100%'
      }
    }} type={'pdf'}>
        <PDFRenderer src={value} downloadFile={downloadFile} />
      </MediaRenderComponent>;
  }
};
export function getCustomRenderer(propertyKey: string) {
  const customRendererKey = Object.keys(CustomRenderer).find(sKey => propertyKey.startsWith(sKey));
  return customRendererKey && CustomRenderer[customRendererKey];
}
const ArrayItemStartEnhancer = () => {
  const [, theme] = useStyletron();
  return <Block style={{
    width: '12px',
    height: '12px',
    padding: '0 5px'
  }}>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="40" fill={theme.colors.contentPrimary} />
      </svg>
    </Block>;
};
const ArrayItemContainer = styled(Block, ({
  $theme
}) => ({
  flex: '1 1 auto',
  marginBottom: $theme.sizing.scale200
}));
const renderArray = (items: Array<JSON>, options = {}, styleOverrides = {}) => {
  return items.map((it, idx) => {
    return <Block style={{
      display: 'flex'
    }}>
        <ArrayItemStartEnhancer />
        {/* @ts-expect-error todo(ts-migration) TS2554 Expected 5 arguments, but got 3. */}
        <ArrayItemContainer>{renderJSON(it, options, styleOverrides)}</ArrayItemContainer>
      </Block>;
  });
};
const rebuildOrders = (json: {
  [key: string]: any;
}) => {
  const {
    metaData,
    ...rest
  } = json;
  const orders = metaData && metaData.orders || [];
  const newJson = {};
  orders.filter(key => json.hasOwnProperty(key)).forEach(key => {
    newJson[key] = json[key];
  });
  return {
    ...newJson,
    ...rest
  };
};
const sanitizeAndParse = (value: any) => {
  // This parses the value in case it's a string. This can be extended to add validation
  // logic as well if needed.
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }
  return value;
};
const renderJSON = (json: JSON, options: OptionsT, styleOverrides, onImageSelection, enableImageSelection, noOfImageGrids, downloadFile) => {
  const {
    ValueContainer
  } = styleOverrides;
  if (json === null || json === undefined) {
    return null;
  }
  if (Array.isArray(json)) {
    return renderArray(json, options, styleOverrides);
  }
  if (isPrimitive(json)) {
    return <StyledValue $overrides={ValueContainer}>
        {/* @ts-expect-error todo(ts-migration) TS2786 'DataComponent' cannot be used as a JSX component. */}
        <DataComponent value={json} options={options} />
      </StyledValue>;
  }
  const rJson = rebuildOrders((json as any));
  const listOfProperties = Object.keys(rJson);
  const updatedListOfProperties = ([] as Array<string | Array<string>>);
  if (noOfImageGrids && noOfImageGrids >= 2) {
    // currently only handled for grid of 2
    for (let i = 0; i < listOfProperties.length; i++) {
      const currentKey = listOfProperties[i];
      const nextKey = listOfProperties[i + 1] || '';
      if (
      // @ts-expect-error todo(ts-migration) TS18048 'currentKey' is possibly 'undefined'.
      currentKey.startsWith(CUSTOM_PROPERTY_PREFIX.IMG) && nextKey.startsWith(CUSTOM_PROPERTY_PREFIX.IMG)) {
        // @ts-expect-error todo(ts-migration) TS2322 Type 'string | undefined' is not assignable to type 'string'.
        updatedListOfProperties.push([currentKey, nextKey]);
        i += noOfImageGrids - 1;
      } else {
        // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'string | undefined' is not assignable to parameter of type 'string | string[]'.
        updatedListOfProperties.push(currentKey);
      }
    }
  }
  const getCustomComponent = (key: string, customStyleOverides: StyleOverrides = {}) => {
    const value = rJson[key];
    const customRenderer = getCustomRenderer(key);
    if (customRenderer) {
      const customComponent = customRenderer(key, value, options, {
        ...styleOverrides,
        ...customStyleOverides
      }, onImageSelection, enableImageSelection, downloadFile);
      return customComponent;
    }
    return null;
  };
  const listOfPropertiesToRender = !!updatedListOfProperties.length ? updatedListOfProperties : listOfProperties;
  const results = listOfPropertiesToRender.map((key: string) => {
    if (Array.isArray(key)) {
      const keyList = (key as Array<string>);
      const components = keyList.map(currentKey => {
        return getCustomComponent(currentKey, {
          ParentContainer: {
            width: `calc(100% / ${noOfImageGrids})`
          }
        });
      });
      return <GridWrapper>{components}</GridWrapper>;
    }
    const customComponent = getCustomComponent(key);
    if (customComponent) {
      return customComponent;
    }
    const value = sanitizeAndParse(rJson[key]);
    if (Array.isArray(value)) {
      return <JSONBlock title={key} styleOverrides={styleOverrides}>
          {renderArray(value, options, styleOverrides)}
        </JSONBlock>;
    }
    if (typeof value === 'object' && value !== null) {
      return <JSONBlock title={key} styleOverrides={styleOverrides}>
          {/* @ts-expect-error todo(ts-migration) TS2554 Expected 5 arguments, but got 3. */}
          {renderJSON(value, options, styleOverrides)}
        </JSONBlock>;
    }
    if (isPrimitive(value)) {
      return <KeyValue title={key} styleOverrides={styleOverrides}>
          {/* @ts-expect-error todo(ts-migration) TS2786 'DataComponent' cannot be used as a JSX component. */}
          <DataComponent value={value} options={options} />
        </KeyValue>;
    }
    return null;
  });
  return results;
};
const config = {
  loader: {
    load: ['[tex]/html']
  },
  tex: {
    packages: {
      '[+]': ['html']
    },
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['$$', '$$'], ['\\[', '\\]']]
  }
};
const JSONViewer = ({
  styleOverrides = {},
  options = {},
  onImageSelection,
  enableImageSelection,
  noOfImageGrids,
  downloadFile,
  ...rest
}: Props) => {
  return <MainContainer>
      {renderJSON(rest, options, styleOverrides, onImageSelection, enableImageSelection, Number(noOfImageGrids), Boolean(downloadFile))}
    </MainContainer>;
};
export default JSONViewer;